import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, CheckCircle2, Sparkles, Quote } from "lucide-react";
import LogoText from "../components/LogoText";
import Brand from "../components/Brand";
import { resolveStyle } from "../lib/funnelStyle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FunnelPublic() {
  const { id } = useParams();
  const nav = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/funnels/${id}/public`)
      .then(r => setFunnel(r.data))
      .catch(() => setError("This funnel is not available."));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/funnels/${id}/optin`, form);
      setDone(true);
      toast.success("Thank you! Check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 text-center">
        <LogoText size="md" />
        <h1 className="font-serif-display text-3xl text-red-wine mt-8">{error}</h1>
        <button onClick={() => nav("/")} className="btn-outline-gold mt-6">Back to <Brand/></button>
      </div>
    );
  }
  if (!funnel) return <div className="min-h-screen flex items-center justify-center text-foreground">Loading…</div>;

  // Don't show drafts to the public — show a Coming Soon page instead
  if (funnel.status === "draft") {
    return (
      <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 text-center" data-testid="funnel-coming-soon">
        <LogoText size="md" />
        <div className="text-[10px] tracking-wider-cap text-gold-deep mt-8 font-bold">A <Brand/> Funnel</div>
        <h1 className="font-serif-display text-4xl md:text-6xl text-red-wine mt-3 uppercase">Coming Soon</h1>
        <div className="h-px w-16 bg-[#F39A1F] my-5"/>
        <p className="text-base text-foreground max-w-md">This page is still being prepared. Please check back soon.</p>
        <button onClick={() => nav("/")} className="btn-outline-gold mt-7">Back to <Brand/></button>
      </div>
    );
  }

  const useSections = Array.isArray(funnel.sections) && funnel.sections.length > 0;
  const bgOpacity = funnel.theme?.bgOpacity ?? 0.25;

  return (
    <div className="min-h-screen bg-ivory" data-testid="funnel-public">
      <header className="border-b border-border/60 bg-background/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-3 flex justify-between items-center">
          <a href="/" className="inline-block"><LogoText size="sm" as="span" /></a>
          <span className="text-[10px] tracking-wider-cap text-gold-deep hidden md:inline">A <Brand/> Funnel</span>
        </div>
      </header>

      {/* HERO IMAGE — ALWAYS at the top of every funnel */}
      <div className="w-full bg-onyx" data-testid="funnel-hero-image">
        <div className="max-w-5xl mx-auto">
          <img src={funnel.image} alt={funnel.name} className="w-full h-[280px] md:h-[420px] object-contain bg-white p-3"/>
        </div>
      </div>

      <section className="bg-ivory">
        {useSections ? (
          <div className="max-w-3xl mx-auto px-6 md:px-8 py-14 md:py-20 space-y-2">
            {funnel.sections.map((b) => <PublicBlock key={b.id} block={b} themeStyle={funnel.theme?.style} funnel={funnel} form={form} setForm={setForm} submit={submit} loading={loading} done={done} />)}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto px-6 md:px-8 py-14 md:py-20">
            <div className="fade-up">
              <div className="text-xs tracking-wider-cap text-gold-deep mb-3 uppercase">{funnel.category} · Free</div>
              <h1 className="font-serif-display text-4xl md:text-6xl text-red-wine uppercase leading-[1.05]">{funnel.headline || funnel.name}</h1>
              <div className="divider-gold !mx-0"></div>
              <p className="text-lg text-foreground mt-4 max-w-2xl">{funnel.subheadline || funnel.description}</p>
              <ul className="mt-6 space-y-2 text-base text-foreground max-w-2xl">
                {(funnel.benefits || []).map((b, i) => (
                  <li key={i} className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-gold-deep shrink-0 mt-0.5"/>{b}</li>
                ))}
              </ul>
              <div className="mt-10 max-w-xl">
                <FormBlock funnel={funnel} form={form} setForm={setForm} submit={submit} loading={loading} done={done} />
              </div>
            </div>
          </div>
        )}
      </section>

      <footer className="border-t border-border/60 bg-white py-8 text-center text-sm text-foreground">
        © {new Date().getFullYear()} <Brand suffix=".com"/> — "Go into all the world and preach the gospel." Mark 16:15
      </footer>

      {/* Powered-by badge — shown ONLY on free-tier (Ministry Seed) members' funnels. */}
      {funnel.show_powered_by && (
        <a
          href="https://faitharama.com/?utm_source=powered_by&utm_medium=funnel_footer"
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-[#0F172A] py-5 text-center hover:bg-[#2A2521] transition border-t-2 border-[#F39A1F]"
          data-testid="powered-by-faitharama"
        >
          <div className="inline-flex items-center gap-3 px-5 py-1">
            <span className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#F39A1F]">Powered by</span>
            <img
              src="/brand/faitharama-logo.png"
              alt="Faitharama"
              className="h-7 w-auto opacity-95"
              loading="lazy"
            />
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#FFFFFF]/70 mt-1">
            Build your own free ministry funnel · faitharama.com
          </div>
        </a>
      )}
    </div>
  );
}

function PublicBlock({ block, themeStyle, funnel, form, setForm, submit, loading, done }) {
  const { sizeCls, colorHex, alignCls, fontFamily } = resolveStyle(block, themeStyle);
  switch (block.type) {
    case "heading":
      return (
        <div className="py-3">
          <h1 className={`${sizeCls} ${alignCls} uppercase leading-[1.05]`} style={{ color: colorHex, fontFamily }}>{block.text}</h1>
          <div className={`h-px w-16 bg-[#F39A1F] mt-3 ${alignCls === "text-center" ? "mx-auto" : alignCls === "text-right" ? "ml-auto" : ""}`}/>
        </div>
      );
    case "paragraph":
      return <p className={`${sizeCls} ${alignCls} py-3`} style={{ color: colorHex, fontFamily }}>{block.text}</p>;
    case "image":
      return <div className="py-3"><img src={block.src} className="w-full rounded-xl object-contain bg-white" style={{height: `${block.height || 280}px`}}/></div>;
    case "video": {
      const vid = block.videoId || (function(u){
        if (!u) return null;
        const s = String(u).trim();
        if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
        const pats = [
          /(?:youtube\.com\/watch\?(?:.*&)?v=)([a-zA-Z0-9_-]{11})/,
          /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
          /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
          /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
        ];
        for (const re of pats) { const m = s.match(re); if (m) return m[1]; }
        return null;
      })(block.url);
      if (!vid) return null;
      return (
        <div className="py-3">
          <div className="relative rounded-xl overflow-hidden bg-black shadow-md" style={{ aspectRatio: "16 / 9" }}>
            <iframe
              src={`https://www.youtube.com/embed/${vid}?rel=0&modestbranding=1`}
              title="Funnel video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      );
    }
    case "product": {
      const url = (block.buyUrl || "").trim();
      const cta = block.cta || "Get It Now";
      const hasImage = !!block.imageSrc;
      return (
        <div className="py-3">
          <div className="rounded-xl border-2 p-5 md:p-7" style={{ background: "#FFFFFF", borderColor: "#2E9F3A" }}>
            <div className={`grid gap-5 items-center ${hasImage ? "md:grid-cols-[160px_1fr_auto]" : "md:grid-cols-[1fr_auto]"}`}>
              {hasImage && (
                <img src={block.imageSrc} alt={block.productName || ""}
                     className="w-full aspect-square object-contain p-2 bg-white rounded-lg shadow-md"
                     loading="lazy"/>
              )}
              <div>
                <div className="font-serif-display text-2xl md:text-3xl text-red-wine font-bold leading-tight">
                  {block.productName || "Your Product"}
                </div>
                {block.productDescription && (
                  <p className="mt-2 text-base text-foreground/85 leading-relaxed">{block.productDescription}</p>
                )}
              </div>
              <div className="text-center md:text-right">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-foreground/60 mb-1">Price</div>
                <div className="font-bold text-4xl text-red-wine">${block.price ?? 0}</div>
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer"
                     className="mt-3 inline-flex items-center gap-2 px-6 py-3 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-md text-white hover:-translate-y-0.5 transition"
                     style={{ background: "#C9202E" }}>
                    {cta}
                  </a>
                ) : (
                  <div className="mt-3 inline-block px-4 py-3 rounded-md text-xs text-foreground/50 italic">
                    (no checkout link set)
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    case "benefits":
      return (
        <ul className={`py-3 space-y-2 ${alignCls === "text-center" ? "max-w-xl mx-auto" : ""}`}>
          {block.items.map((it, i) => (
            <li key={i} className={`flex items-start gap-2 ${sizeCls} ${alignCls === "text-center" ? "justify-center" : alignCls === "text-right" ? "justify-end" : ""}`} style={{ color: colorHex, fontFamily }}>
              <CheckCircle2 className="w-5 h-5 text-gold-deep mt-0.5 flex-shrink-0"/>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <div className="py-5 px-6 bg-white/70 border-l-4 border-[#F39A1F] my-3 rounded-r-lg">
          <Quote className="w-6 h-6 text-gold-deep mb-2"/>
          <p className={`${sizeCls} ${alignCls} italic`} style={{ color: colorHex, fontFamily }}>{block.text}</p>
          {block.author && <p className={`text-sm text-foreground mt-2 ${alignCls}`} style={{ fontFamily }}>{block.author}</p>}
        </div>
      );
    case "form":
      return <FormBlock funnel={funnel} form={form} setForm={setForm} submit={submit} loading={loading} done={done} customCta={block.cta} customNote={block.note}/>;
    default: return null;
  }
}

function FormBlock({ funnel, form, setForm, submit, loading, done, customCta, customNote }) {
  const [upsellAccepted, setUpsellAccepted] = useState(false);
  if (done) {
    return (
      <div className="faith-card p-8 border-2 text-center my-3" style={{borderColor:'#F39A1F'}} data-testid="funnel-success">
        <CheckCircle2 className="w-12 h-12 text-gold-deep mx-auto" strokeWidth={1.5}/>
        <div className="mt-4 text-3xl uppercase text-red-wine">Thank you, {form.name.split(" ")[0]}!</div>
        <p className="text-base text-foreground mt-3">Check your inbox — we sent the details just now.</p>
        {funnel.upsell_name && (
          <div className="mt-6 rounded-xl bg-white border-2 border-[#F39A1F] overflow-hidden text-left shadow-2xl" data-testid="funnel-upsell">
            {funnel.upsell_image && (
              <img src={funnel.upsell_image} alt={funnel.upsell_name} className="w-full h-56 md:h-64 object-contain bg-white p-3"/>
            )}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-gold-deep"/>
                <div className="text-[10px] tracking-wider-cap text-gold-deep font-bold uppercase">★ Special One-Time Offer ★</div>
              </div>
              <div className="font-serif-display text-3xl uppercase text-red-wine leading-tight">{funnel.upsell_name}</div>
              <div className="font-serif-display text-2xl text-gold-deep mt-1">${Number(funnel.upsell_price).toFixed(2)}</div>
              {funnel.upsell_description && <p className="text-base text-foreground mt-3">{funnel.upsell_description}</p>}

              {/* Checkbox + CTA */}
              <label className="mt-5 flex items-start gap-3 cursor-pointer p-3 rounded-md bg-white border-2 border-[#F39A1F]/40 hover:border-[#F39A1F] transition" data-testid="funnel-upsell-checkbox-wrap">
                <input
                  type="checkbox"
                  checked={upsellAccepted}
                  onChange={e => setUpsellAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#2E9F3A] cursor-pointer"
                  data-testid="funnel-upsell-checkbox"
                />
                <span className="text-base text-foreground font-bold">
                  ✓ Yes, add <span className="text-red-wine">{funnel.upsell_name}</span> to my order for <span className="text-red-wine">${Number(funnel.upsell_price).toFixed(2)}</span>
                </span>
              </label>

              {upsellAccepted ? (
                <a
                  href={`mailto:team@faitharama.com?subject=Yes! I want ${encodeURIComponent(funnel.upsell_name)}&body=Hi! I want to grab the upsell "${funnel.upsell_name}" for $${Number(funnel.upsell_price).toFixed(2)}. My email: ${form.email}. Please send me payment details!`}
                  className="block mt-4 bg-red-wine text-white py-4 rounded-md font-bold uppercase tracking-wider-cap text-base text-center hover:bg-red-wine/90 transition shadow-xl"
                  data-testid="funnel-upsell-yes"
                >
                  YES — I WANT THIS · CONTINUE →
                </a>
              ) : (
                <button
                  disabled
                  className="block w-full mt-4 bg-foreground/10 text-foreground/60 py-4 rounded-md font-bold uppercase tracking-wider-cap text-base text-center cursor-not-allowed"
                >
                  ☑ Check the box above to continue
                </button>
              )}
              <button onClick={() => window.location.href = "/"} className="block w-full mt-2 text-foreground/70 text-sm underline" data-testid="funnel-upsell-no">No thanks, I'm good for now</button>

              <div className="text-[10px] tracking-wider-cap text-foreground/60 text-center mt-4 font-bold uppercase">⚠ Payment processing coming soon — for now we'll email you to complete the order</div>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="faith-card p-7 border-2 my-3 bg-white" style={{borderColor:'#F39A1F'}} data-testid="funnel-form">
      <div className="flex items-center gap-2 mb-3">
        <Lock className="w-4 h-4 text-gold-deep"/>
        <div className="text-[11px] font-overline uppercase tracking-wider-cap text-gold-deep font-bold">Get Yours Free</div>
      </div>
      <div className="grid gap-3 mt-3">
        <input required placeholder="Your name" className="px-4 py-3 rounded-md border border-border bg-white text-base" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="funnel-name"/>
        <input required type="email" placeholder="your@email.com" className="px-4 py-3 rounded-md border border-border bg-white text-base" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="funnel-email"/>
      </div>
      <button disabled={loading} type="submit" className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2" data-testid="funnel-submit">
        <Mail className="w-4 h-4"/>{loading ? "Sending…" : (customCta || funnel.cta_text || "Send It to Me")}
      </button>
      <div className="text-[11px] tracking-wider-cap text-foreground mt-3 text-center">{customNote || "No spam · Unsubscribe anytime · Faitharama.com"}</div>
    </form>
  );
}
